import { useTranslation } from 'react-i18next';
import { formatDay, formatPrice } from '@/lib/format-utils';
import { PAYMENT_METHOD_COLORS, PAYMENT_STATUS_COLORS } from '@turizi/shared';
import { Badge, Button, Modal, Group, Text } from '@mantine/core';
import { MoneyTransaction } from '@/types/money-models';
import { Link } from 'react-router-dom';
import { BUSINESSES_PATH, PERSONS_PATH } from '@/lib/navigation';
import { IconTrash } from '@tabler/icons-react';
import { useDeleteMoneyTransaction } from '@/hooks/api-hooks';
import { useState } from 'react';
import { useDisclosure } from '@mantine/hooks';


interface Props {
    moneyTransactions: MoneyTransaction[];
    title?: string;
    onDelete?: (id: number) => void;
}


export default function MoneyTransactionsTable({
    moneyTransactions,
    title,
    onDelete,
}: Props): JSX.Element {
    const { t } = useTranslation();
    const deleteMoneyTransactionApi = useDeleteMoneyTransaction();
    const [deletingId, setDeletingId] = useState<string | number | null>(null);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | number | null>(null);
    const [opened, { toggle, close }] = useDisclosure(false);

    const linkUrl = (transaction: MoneyTransaction) => {
        if (transaction.from_person_id || transaction.to_person_id) {
            return `${PERSONS_PATH}/${transaction.from_person_id || transaction.to_person_id}?tab=client_history`;
        }
        if (transaction.from_business_id || transaction.to_business_id) {
            return `${BUSINESSES_PATH}/${transaction.from_business_id || transaction.to_business_id}?tab=client_history`;
        }
        return null;
    };

    const confirmDelete = () => {
        if (pendingDeleteId) {
            if (onDelete) {
                onDelete(Number(pendingDeleteId));
            } else {
                setDeletingId(pendingDeleteId);
                deleteMoneyTransactionApi.mutate(pendingDeleteId, {
                    onSettled: () => setDeletingId(null)
                });
            }
            close();
            setPendingDeleteId(null);
        }
    };

    const onDeleteClick = (id: string | number) => {
        setPendingDeleteId(id);
        toggle();
    };

    return (
        <div>
            <Modal opened={opened} onClose={close} title={t('delete_transaction')} centered>
                <Text size="sm">{t('are_you_sure_delete_transaction')}</Text>
                <div className="flex justify-between mt-8">
                    <Button variant="default" onClick={close} size='xs'>
                        {t('cancel')}
                    </Button>
                    <Button color="red" onClick={confirmDelete} size='xs'>
                        {t('confirm')}
                    </Button>
                </div>
            </Modal>

            {title && (
                <div className="text-lg font-semibold mb-1">
                    {title}
                </div>
            )}
            <div className="subtle-table mt-4">
                <table>
                    <thead>
                        <tr>
                            <th>{t('status')}</th>
                            <th>{t('date')}</th>
                            <th>{t('origin')}</th>
                            <th>{t('destination')}</th>
                            <th>{t('payment_method')}</th>
                            <th>{t('reason')}</th>
                            <th className="number">{t('amount')}</th>
                            <th>{t('notes')}</th>
                            <th>{t('actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {moneyTransactions
                            ?.sort((a, b) => new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime())
                            ?.map((transaction) => {
                                const url = linkUrl(transaction);
                                return (
                                    <tr key={`transaction-${transaction.id}`} className="clickable-row">
                                        <td>
                                            {url && (
                                                <Link
                                                    to={url}
                                                    className="main-link"
                                                    target="_blank"
                                                />
                                            )}
                                            <Badge
                                                variant="light"
                                                color={PAYMENT_STATUS_COLORS[transaction.status]}
                                                size="xs"
                                            >
                                                {t(transaction.status) || '-'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div className='font-semibold text-gray-400 whitespace-nowrap'>
                                                {formatDay(transaction.payment_date)}
                                            </div>
                                        </td>
                                        <td>
                                            {transaction.from_person?.name || transaction.from_business?.name || '-'}
                                        </td>
                                        <td>
                                            {transaction.to_person?.name || transaction.to_business?.name || '-'}
                                        </td>
                                        <td>
                                            <Badge
                                                variant="light"
                                                color={PAYMENT_METHOD_COLORS[transaction.payment_method]}
                                                size="xs"
                                            >
                                                {t(transaction.payment_method) || '-'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div className="text-gray-400 text-xs">
                                                {t(`money_transaction_reasons.${transaction.reason || 'payment'}`) || '-'}
                                            </div>
                                        </td>
                                        <td className="number font-semibold whitespace-nowrap">
                                            {formatPrice(transaction.amount)} <span className="text-gray-400 text-xs font-normal">{transaction.currency}</span>
                                        </td>
                                        <td>
                                            {transaction.notes}
                                        </td>
                                        <td>
                                            <Button
                                                variant="subtle"
                                                color="red"
                                                size="xs"
                                                loading={deleteMoneyTransactionApi.isPending && deletingId === transaction.id}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDeleteClick(transaction.id);
                                                }}
                                                className="relative z-10"
                                            >
                                                <IconTrash size={14} stroke={1} />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
